import React, { useCallback } from 'react';
import { Color } from '@diritto/common/style/color';
import { PaginationArrow } from 'icons/PaginationArrow';
import { tm } from 'utils/tm';

const variant = {
  button:
    'rounded-lg px-[13px] py-[6px] flex items-center justify-center bg-transparent transition-colors focus:outline-none',
};

interface SectionContentPagerProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const SectionContentPager = ({ currentPage, totalPages, onPageChange }: SectionContentPagerProps) => {
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const handlePrevious = useCallback(() => {
    if (canGoPrevious) {
      onPageChange(currentPage - 1);
    }
  }, [canGoPrevious, currentPage, onPageChange]);

  const handleNext = useCallback(() => {
    if (canGoNext) {
      onPageChange(currentPage + 1);
    }
  }, [canGoNext, currentPage, onPageChange]);

  return (
    <div className="flex flex-row items-center justify-center pt-[10px]">
      <button
        type="button"
        onClick={handlePrevious}
        disabled={!canGoPrevious}
        style={{ border: `1px solid ${Color.Gray300}` }}
        className={tm(variant.button, canGoPrevious ? 'cursor-pointer pc:hover:bg-gray-100' : 'cursor-default')}>
        <PaginationArrow
          direction="left"
          color={Color.Gray600}
          className={tm('size-[25px]', !canGoPrevious ? 'opacity-30' : '')}
        />
      </button>
      <div className="flex flex-row items-center justify-center min-w-[75px] text-xxs font-medium">
        <span className="text-primary-500">{currentPage}</span>
        <span className="text-black/30">/</span>
        <span className="text-black/30">{totalPages}</span>
      </div>
      <button
        type="button"
        onClick={handleNext}
        disabled={!canGoNext}
        style={{ border: `1px solid ${Color.Gray300}` }}
        className={tm(variant.button, canGoNext ? 'cursor-pointer pc:hover:bg-gray-100' : 'cursor-default')}>
        <PaginationArrow
          direction="right"
          color={Color.Gray600}
          className={tm('size-[25px]', !canGoNext ? 'opacity-30' : '')}
        />
      </button>
    </div>
  );
};
